(() => {
  const MIN_AGE = 21;
  const ATTR_VERIFIED = 'age_21_verified';
  const ATTR_VERIFIED_AT = 'age_21_verified_at';

  const CART_CACHE_MS = 300;
  let cartCache = null;
  let cartCacheAt = 0;

  const debounce = (fn, ms) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), ms);
    };
  };

  const calculateAge = (dobStr) => {
    if (!dobStr) return null;
    const dob = new Date(dobStr + 'T00:00:00');
    if (Number.isNaN(dob.getTime())) return null;

    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
    return age;
  };

  const getCart = async () => {
    const now = Date.now();
    if (cartCache && now - cartCacheAt < CART_CACHE_MS) return cartCache;

    const url =
      window.Shopify?.routes?.root
        ? window.Shopify.routes.root + 'cart.js'
        : '/cart.js';

    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    const cart = await res.json();
    cartCache = cart;
    cartCacheAt = now;
    return cart;
  };

  const updateCartAttributes = async (attributes) => {
    const url =
      window.Shopify?.routes?.root
        ? window.Shopify.routes.root + 'cart/update.js'
        : '/cart/update.js';

    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    });

    const cart = await res.json();
    cartCache = cart;
    cartCacheAt = Date.now();
    return cart;
  };

  const getDrawerRoot = () => {
    // Keep scope tight to avoid performance issues
    return (
      document.querySelector('.cart-drawer__dialog') ||
      document.querySelector('cart-drawer-component') ||
      document
    );
  };

  const getUI = (root) => {
    const scope = root || document;
    return {
      checkbox: scope.querySelector('#ageVerificationCheckbox'),
      dob: scope.querySelector('#ageVerificationDob'),
      err: scope.querySelector('#ageVerificationError'),
      checkoutBtn: scope.querySelector('#checkout'),
      accelerated: scope.querySelector('.additional-checkout-buttons'),
    };
  };

  const setDisabled = (el, disabled) => {
    if (!el) return;
    el.disabled = !!disabled;
    el.style.opacity = disabled ? '0.5' : '1';
    el.style.pointerEvents = disabled ? 'none' : 'auto';
  };

  const setAcceleratedDisabled = (root, disabled) => {
    const ui = getUI(root);
    if (!ui.accelerated) return;
    ui.accelerated.style.opacity = disabled ? '0.5' : '1';
    ui.accelerated.style.pointerEvents = disabled ? 'none' : 'auto';
    ui.accelerated.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  };

  const isCartVerified = (cart) => cart?.attributes?.[ATTR_VERIFIED] === 'true';

  const renderState = (root, ok, message) => {
    const ui = getUI(root);
    if (ui.err) ui.err.textContent = message || '';
    setDisabled(ui.checkoutBtn, !ok);
    setAcceleratedDisabled(root, !ok);
  };

  const validate = (root) => {
    const ui = getUI(root);
    const dobVal = ui.dob?.value || '';
    const checked = !!ui.checkbox?.checked;

    if (!dobVal) return { ok: false, message: 'Please enter your date of birth to continue.' };

    const age = calculateAge(dobVal);
    if (age === null) return { ok: false, message: 'Please enter a valid date of birth.' };
    if (age < MIN_AGE) return { ok: false, message: 'You must be 21+ to purchase.' };
    if (!checked) return { ok: false, message: '' };

    return { ok: true, message: '' };
  };

  // Runs whenever the drawer content changes or opens:
  const applyGateFromCart = async () => {
    const root = getDrawerRoot();
    const ui = getUI(root);

    // If button doesn’t exist yet, nothing to toggle—MutationObserver will call again when it appears.
    if (!ui.checkoutBtn) return;

    // Fail closed immediately
    setDisabled(ui.checkoutBtn, true);
    setAcceleratedDisabled(root, true);

    try {
      const cart = await getCart();
      if (isCartVerified(cart)) {
        renderState(root, true, '');
      } else {
        // Clear UI fields so they must re-verify for new carts
        if (ui.checkbox) ui.checkbox.checked = false;
        if (ui.dob) ui.dob.value = '';
        renderState(root, false, '');
      }
    } catch (e) {
      renderState(root, false, 'Unable to verify cart status. Please try again.');
    }
  };

  const syncState = async () => {
    const root = getDrawerRoot();
    const v = validate(root);

    // Update UI immediately
    renderState(root, v.ok, v.message);

    if (!v.ok) {
      // Best-effort clear
      try {
        await updateCartAttributes({ [ATTR_VERIFIED]: '', [ATTR_VERIFIED_AT]: '' });
      } catch (_) {}
      return;
    }

    // Stamp cart, then enable
    try {
      await updateCartAttributes({
        [ATTR_VERIFIED]: 'true',
        [ATTR_VERIFIED_AT]: new Date().toISOString(),
      });
      renderState(root, true, '');
    } catch (e) {
      renderState(root, false, 'Could not save verification. Please try again.');
    }
  };

  const syncStateDebounced = debounce(syncState, 150);

  // Event delegation so it works even when Horizon injects/replaces the markup
  document.addEventListener('input', (e) => {
    if (e.target && e.target.id === 'ageVerificationDob') syncStateDebounced();
  });

  document.addEventListener('change', (e) => {
    if (!e.target) return;
    if (e.target.id === 'ageVerificationDob' || e.target.id === 'ageVerificationCheckbox') {
      syncStateDebounced();
    }
  });

  // Watch only the drawer region for injected checkout button / age UI
  const setupObserver = () => {
    const root = getDrawerRoot();
    if (!root || root === document) return;

    if (root.dataset.ageGateObserverAttached === 'true') return;
    root.dataset.ageGateObserverAttached = 'true';

    const obs = new MutationObserver(debounce(applyGateFromCart, 50));
    obs.observe(root, { childList: true, subtree: true });

    // Run once now
    applyGateFromCart();
  };

  document.addEventListener('DOMContentLoaded', () => {
    setupObserver();

    // If Horizon emits an open event, great—re-apply
    const drawer = document.querySelector('cart-drawer-component');
    drawer?.addEventListener('open', () => {
      setupObserver();
      applyGateFromCart();
    });

    // Fallback: re-try attaching observer shortly after load in case drawer mounts late
    setTimeout(setupObserver, 500);
    setTimeout(setupObserver, 1500);
  });
})();